'use client';

import { QueryClientProvider } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { ApiProvider } from '@/contexts/ApiContext';
import { createQueryClient } from '@/lib/queryClient';
import { RequestLanguageContext, detectAndApplyBrowserLanguage, useLanguageStore } from '@/stores/languageStore';
import { useAuthStoreHydrate } from '@/stores/authStore';
import { WebUrlNavigationProvider } from '@/components/WebUrlNavigationProvider';
import { ConfirmDialogHost } from '@/components/ConfirmDialog';
import { useYandexMetrika } from '@/hooks/useYandexMetrika';
import { useFacebookPixel } from '@/hooks/useFacebookPixel';
import { isWikiPath } from '@/lib/wikiPath';

// WHY: вики — статьи, а не игра: заставку Rush Mid и звуки тянем, только когда посетитель ушёл в игровые разделы; модули
// грузятся тем же импортом по требованию, чтобы их не было в общем бандле страниц вики
function GameAssetsBootstrap() {
  const onWiki = isWikiPath(usePathname());

  useEffect(() => {
    if (onWiki) return;
    void Promise.all([import('@/lib/prefetchLoadingAssets'), import('@/lib/audioSession'), import('@/lib/uiSounds')]).then(
      ([assets, audio, sounds]) => {
        assets.prefetchLoadingAssets();
        audio.initAudioSession();
        sounds.preloadUiSounds();
        sounds.initClickSound();
      },
    );
  }, [onWiki]);

  return null;
}

export function Providers({ children }: { children: ReactNode }) {
  const queryClient = useMemo(() => createQueryClient(), []);
  const requestLanguage = useContext(RequestLanguageContext);
  useAuthStoreHydrate();
  useYandexMetrika();
  useFacebookPixel();

  useEffect(() => {
    // WHY: на вебе язык задаёт адрес, а кэшированный HTML вики приходит без Set-Cookie — куку языка ставим из адреса
    if (requestLanguage) {
      useLanguageStore.getState().setLanguage(requestLanguage);
    } else {
      detectAndApplyBrowserLanguage();
    }
  }, [requestLanguage]);

  return (
    <QueryClientProvider client={queryClient}>
      <ApiProvider>

        <WebUrlNavigationProvider>
          <GameAssetsBootstrap />
          {children}

          <ConfirmDialogHost />
        </WebUrlNavigationProvider>
      </ApiProvider>
    </QueryClientProvider>
  );
}
